import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, Factory, Phone, FileText } from 'lucide-react';
import { toast } from 'sonner';

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    cnpj: ''
  });

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await api.suppliers.getAll();
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Erro ao carregar fornecedores');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openDialog = (supplier = null) => {
    if (supplier) {
      setEditingSupplier(supplier);
      setFormData({
        name: supplier.name,
        phone: supplier.phone || '',
        cnpj: supplier.cnpj || ''
      });
    } else {
      setEditingSupplier(null);
      setFormData({ name: '', phone: '', cnpj: '' });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('O nome do fornecedor é obrigatório');
      return;
    }

    try {
      if (editingSupplier) {
        await api.suppliers.update(editingSupplier.id, formData);
        toast.success('Fornecedor atualizado com sucesso');
      } else {
        await api.suppliers.create(formData);
        toast.success('Fornecedor registado com sucesso');
      }
      setDialogOpen(false);
      fetchSuppliers();
    } catch (error) {
      toast.error('Erro ao guardar fornecedor');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem a certeza que deseja remover este fornecedor?')) {
      try {
        await api.suppliers.delete(id);
        toast.success('Fornecedor removido');
        fetchSuppliers();
      } catch (error) {
        toast.error('Erro ao remover fornecedor. Apenas administradores podem apagar registos.');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <p className="text-muted-foreground font-mono">A carregar fornecedores...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <h1 className="text-5xl font-bold uppercase tracking-tight">Fornecedores</h1>
        <Button 
          onClick={() => openDialog()} 
          className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase h-12 px-6 w-full sm:w-auto"
        >
          <Plus className="mr-2 h-5 w-5" /> Novo Fornecedor
        </Button>
      </div>

      <Card className="bg-card border-border">
        <CardHeader className="border-b border-border/50 pb-4">
          <CardTitle className="text-xl uppercase tracking-wide flex items-center gap-2">
            <Factory className="h-5 w-5 text-primary" />
            Lista de Distribuidores e Parceiros
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {suppliers.length === 0 ? (
            <div className="text-center py-12">
              <Factory className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
              <p className="text-muted-foreground uppercase tracking-widest text-sm">Nenhum fornecedor registado</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground font-mono uppercase text-xs">Fornecedor</TableHead>
                  <TableHead className="text-muted-foreground font-mono uppercase text-xs hidden md:table-cell">Contacto</TableHead>
                  <TableHead className="text-muted-foreground font-mono uppercase text-xs hidden sm:table-cell">CNPJ / NIF</TableHead>
                  <TableHead className="text-muted-foreground font-mono uppercase text-xs text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suppliers.map((supplier) => (
                  <TableRow key={supplier.id} className="border-border/50 hover:bg-secondary/50 transition-colors">
                    <TableCell className="font-bold text-foreground uppercase tracking-wide">
                      {supplier.name}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {supplier.phone ? (
                        <span className="flex items-center gap-2 text-sm text-muted-foreground font-mono">
                          <Phone className="h-3 w-3" /> {supplier.phone}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell font-mono text-sm">
                      {supplier.cnpj ? (
                        <span className="flex items-center gap-2 text-muted-foreground">
                          <FileText className="h-3 w-3" /> {supplier.cnpj}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => openDialog(supplier)}
                          className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDelete(supplier.id)}
                          className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-2xl uppercase font-black tracking-tight text-primary">
              {editingSupplier ? 'Editar Fornecedor' : 'Novo Fornecedor'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Nome da Empresa / Distribuidora *</Label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Ex: Ambev, Frigorífico Silva..."
                className="font-mono bg-secondary/30 border-border focus-visible:ring-primary"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Telefone / WhatsApp</Label>
              <Input
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="(51) 99999-9999"
                className="font-mono bg-secondary/30 border-border focus-visible:ring-primary"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">CNPJ</Label>
              <Input
                name="cnpj"
                value={formData.cnpj}
                onChange={handleInputChange}
                placeholder="00.000.000/0000-00"
                className="font-mono bg-secondary/30 border-border focus-visible:ring-primary"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDialogOpen(false)}
              className="font-bold uppercase border-border hover:bg-secondary"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleSubmit}
              className="bg-primary hover:bg-primary/90 font-bold uppercase"
            >
              {editingSupplier ? 'Guardar' : 'Registar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Suppliers;
